// ============================================================
// attack/prompts - answering the two prompts an attack can pause on.
//
// Phase 6: the re-roll of a miss (Blademaster / Sure Strike / a Doomed
// target). Phase 7: the Furious Assault bonus attack. Each resumes the
// flow the prompt paused: a miss written, or a hit handed to
// `attack/shot`.
// ============================================================
import { getAttackConditionModifiers } from '../../engine/conditionModifiers';
import { beatKindFacts } from './beatFacts';
import { rollD100, calcDoF, calcModifiedSkill } from '../../engine/index';
import type { Slot, Tab } from '../../types';
import type { AttackContext } from './context';
import { settleAttack, writeMissNarrative, type MissRecord } from './outcome';
import { resumeAttackFromHit } from './shot';
import type { PendingBonusAttack, PendingReroll } from './types';

/** The miss the prompt paused on, as it would be written with this roll and these notes. */
function missFrom(pr: PendingReroll, attackRoll: number, dof: number, notes: string[]): MissRecord {
  return {
    tabId: pr.tabId,
    attackRoll,
    modifiedSkill: pr.modifiedSkill,
    dof,
    slot: pr.slot,
    notes,
    overheatDmg: pr.overheatDmg,
    cdResult: pr.cdResult,
    targetName: pr.targetName,
    woundsAfter: pr.woundsAfter,
    woundsMax: pr.woundsMax,
    beatKind: pr.beatKind,
  };
}

/**
 * The user answers the re-roll prompt. Declined: the original miss is
 * written. Accepted: a fresh d100 - a second miss must be accepted (DW
 * RAW), a hit resumes the full hit -> damage -> defence flow.
 */
export function handleRerollChoice(ctx: AttackContext, accept: boolean): void {
  const pr = ctx.pendingReroll;
  if (!pr) return;
  ctx.setPendingReroll(null);
  const tab = ctx.tabs.find((t) => t.id === pr.tabId);
  if (!tab) return;
  // However it ends, the attack is settled HERE, not when the prompt opened (attack/outcome).
  const settle = (hit: boolean) => settleAttack(ctx, pr.tabId, { hit, slot: pr.slot, isMelee: pr.isMelee });
  if (!accept) {
    writeMissNarrative(ctx, missFrom(pr, pr.originalRoll, pr.dof, pr.specialNotes));
    return settle(false);
  }

  const reroll = rollD100();
  const talentNote = pr.talentName + ': Re-rolled (' + pr.originalRoll + ' → ' + reroll + ')';
  const rerollHit = reroll <= pr.modifiedSkill;
  if (!rerollHit) {
    const notes = [...pr.specialNotes, talentNote + ' - still missed.'];
    writeMissNarrative(ctx, missFrom(pr, reroll, calcDoF(reroll, pr.modifiedSkill), notes));
    return settle(false);
  }
  resumeAttackFromHit(ctx, {
    attackRoll: reroll,
    modifiedSkill: pr.modifiedSkill,
    slot: pr.slot,
    notes: [...pr.specialNotes, talentNote + ' - hit!'],
    overheatDmg: pr.overheatDmg,
    cdResult: pr.cdResult,
    tabId: pr.tabId,
    tab,
    isBonusAttack: false,
    allOut: pr.allOut,
    skillBreakdown: pr.skillBreakdown,
  });
  settle(true);
}

/**
 * The bonus attack's modified skill.
 *
 * A STANDARD attack: no All-Out on it. Nothing to subtract (v8.310.7) - the
 * swing that earned it spent the All-Out toggle, so the caller's mod no
 * longer carries the +20. Same attacker, same target, so the same
 * condition modifiers apply.
 */
function bonusSkill(ctx: AttackContext, slot: Slot, tab: Tab): number {
  const condMods = ctx.applyConditionModifiers
    ? getAttackConditionModifiers(ctx.actorConditionContext ?? { conditions: [] }, tab.target, true, ctx.condModOptions)
    : { total: 0, labels: [] };
  return calcModifiedSkill(
    slot,
    ctx.attackMod + condMods.total,
    tab.target.size,
    ctx.rangeBand,
    true,
    ctx.hatred,
    ctx.frenzied,
    ctx.attackerCharStats?.removeLongRangePenalty ?? false,
    ctx.attackerCharStats?.removeExtremeRangePenalty ?? false,
  );
}

/** The bonus swing itself: a miss written, or a hit resumed (isBonusAttack: single mode, no Hammer Blow). */
function swingBonus(ctx: AttackContext, pba: PendingBonusAttack, tab: Tab): void {
  const slot = pba.slot;
  const notes: string[] = ['Furious Assault: Bonus Standard Attack (Reaction spent).'];
  const skill = bonusSkill(ctx, slot, tab);
  const roll = rollD100();
  const bonusHit = roll <= skill;
  if (!bonusHit) {
    const targetName = tab.target.name || tab.label || '';
    const { current: woundsAfter, max: woundsMax } = tab.target.wounds;
    const dof = calcDoF(roll, skill);
    const miss = { tabId: pba.tabId, attackRoll: roll, modifiedSkill: skill, dof, slot, notes, overheatDmg: 0 };
    // A bonus attack is always single (engine/attackCalc `hitMode`).
    const beatKind = beatKindFacts(ctx, slot.weaponType === 'melee', 'single');
    return writeMissNarrative(ctx, { ...miss, cdResult: null, targetName, woundsAfter, woundsMax, beatKind });
  }
  const hit = { attackRoll: roll, modifiedSkill: skill, slot, notes, overheatDmg: 0, cdResult: null };
  resumeAttackFromHit(ctx, { ...hit, tabId: pba.tabId, tab, isBonusAttack: true });
}

/** The user answers the Furious Assault prompt: declined is a line in the log; accepted is one more swing. */
export function handleBonusAttackChoice(ctx: AttackContext, accept: boolean): void {
  const pba = ctx.pendingBonusAttack;
  if (!pba) return;
  ctx.setPendingBonusAttack(null);
  if (!accept) {
    ctx.addNarrative(pba.tabId, {
      type: 'info',
      text: 'Furious Assault: Declined bonus attack.',
      timestamp: Date.now(),
    });
    return;
  }
  const tab = ctx.tabs.find((t) => t.id === pba.tabId);
  if (!tab) return;
  ctx.capturePreActionSnapshot();
  ctx.pushHistory();
  swingBonus(ctx, pba, tab);
}
